import React, { useCallback, useEffect, useRef, useState } from "react";
import { MaterialPicker, HuePicker } from "react-color";
import { Link, Prompt } from "react-router-dom";

import landingEmptyLogo from "images/landing-page/landing-empty-logo.png";
import landingPage from "images/landing-page/landing-page.png";
import landingPreviewBody from "images/landing-page/landing-preview-body.png";
import landingPreviewHeaderBG from "images/landing-page/landing-preview-header-bg.png";
import landingPreviewHeader from "images/landing-page/landing-preview-header.png";

import client, { signUpload } from "@culturehq/client";
import { Button, Form, Icon, ImageField, Loader, StringField, SubmitButton, Tooltip } from "@culturehq/components";

import { useProfile } from "app/context";
import { useFetch, useFieldsLabels } from "app/hooks";
import { canViewCompaniesField } from "app/permissions";
import { getOrganizationSubdomain, getImage, getTags, t, Trans } from "app/utils";

import Accordian from "components/common/Accordian";
import Tags from "components/common/Tags";
import imagesSizes from "constants/imagesSizes";
import ErrorMessage from "components/common/ErrorMessage";
import { Time } from "components/common/Time";
import getText from "components/editor/getText";
import LazyEditor from "components/editor/LazyEditor";
import LazyEditorOutput from "components/editor/LazyEditorOutput";
import ChqModalConfirm from "components/common/ChqModalConfirm";
import ApproveProfileModal from "./ApproveProfileModal";
import ApproveStoryModal from "./ApproveStoryModal";
import UsersSlider from "./UsersSlider";
import CopyLink from "./CopyLink";
import DisplayUsers from "./DisplayUsers";
import DisplayStories from "./DisplayStories";
import EmbedStories from "./EmbedStories";

const EmptyState = ({ onCreateLandingPage }) => (
  <div className="LandingPage--Empty">
    <h1 className="LandingPage--Title">
      <Trans>
        Create your first page to start showcasing your people and culture to candidates
      </Trans>
    </h1>
    <a className="LandingPage--Link" href="https://demo.culturehq.com" target="_blank" rel="noopener noreferrer">
      <Icon icon="android-open" />
      <Trans>View an example page here</Trans>
    </a>
    <Button className="LandingPage--Create_Btn" onClick={onCreateLandingPage}>
      <Icon icon="ios-plus" />
      <Trans>Create Page</Trans>
    </Button>
    <img className="LandingPage--Image" src={landingPage} alt="Landing page example" />
  </div>
);

const ApproveSection = ({
  landingPage, onSelectedStories, onSelectedUsers, setApprovingContent, onLandingPageUpdated
}) => (
  <div className="row Approve">
    <div className="col-xs-12">
      <h4 className="Approve-Header"><Trans>Select Profile Spotlights</Trans></h4>
      <ApproveProfileModal
        landingPage={landingPage}
        onSelectedUsers={onSelectedUsers}
        onLandingPageUpdated={onLandingPageUpdated}
      />
      <h4 className="Approve-Header"><Trans>Select Story Spotlights</Trans></h4>
      <ApproveStoryModal
        landingPage={landingPage}
        onSelectedStories={onSelectedStories}
        setApprovingContent={setApprovingContent}
      />
    </div>
  </div>
);

const DesingSection = ({ editorRef, error, onChange, landingPage }) => {
  const [selectedLogo, setSelectedLogo] = useState(landingPage?.logoUrl);
  const [selectedHeaderImage, setSelectedHeaderImage] = useState(landingPage?.headerImageUrl);
  const [headerText, setHeaderText] = useState(landingPage?.headerText);
  const [welcomeNote, setWelcomeNote] = useState(landingPage?.welcomeNote);
  const [color, setColor] = useState(landingPage?.primaryColor || "#79b17d");

  const handleOnLogoChange = image => {
    onChange("logoUrl", image);
    setSelectedLogo(image);
  };

  const handleOnHeaderImageChange = image => {
    onChange("headerImageUrl", image);
    setSelectedHeaderImage(image);
  };

  const getAltImageText = () => (selectedLogo?.alt_description || "Preview");

  const handleChange = (color, _) => {
    onChange("primaryColor", color.hex);
    setColor(color.hex);
  };

  const onHeaderTextChange = value => {
    onChange("headerText", value);
    setHeaderText(value);
  };

  const onWelcomeNoteChange = value => {
    onChange("welcomeNote", value);
    setWelcomeNote(value);
  };

  const getSelectedHeaderImage = () => {
    if (typeof selectedHeaderImage === "string") {
      return selectedHeaderImage;
    }

    return URL.createObjectURL(selectedHeaderImage);
  };

  const getSelectedLogo = () => {
    if (typeof selectedLogo === "string") {
      return selectedLogo;
    }

    return URL.createObjectURL(selectedLogo);
  };

  return (
    <div className="row Design">
      <div className="col-md-6">
        {error && <ErrorMessage error={error} />}
        <div className="logo-field">
          <h4><Trans>Logo*</Trans></h4>
          <ImageField
            aspectRatio={1}
            buttonLabel="Upload logo"
            className={selectedLogo && "selected-image"}
            imageAsBackground
            name="logo"
            onChange={handleOnLogoChange}
            required
            value={getImage(imagesSizes.NORMAL, selectedLogo)}
            alt={getAltImageText()}
          />
        </div>
        <div className="header-field">
          <h4><Trans>Header Image*</Trans></h4>
          <ImageField
            aspectRatio={5}
            buttonLabel="Upload an image"
            className={selectedHeaderImage && "selected-image"}
            imageAsBackground
            name="headerImage"
            onChange={handleOnHeaderImageChange}
            required
            value={getImage(imagesSizes.NORMAL, selectedHeaderImage)}
            alt={getAltImageText()}
          />
        </div>
        <h4 className="header-text-field"><Trans>Header Text*</Trans></h4>
        <StringField max="45" name="headerText" onChange={onHeaderTextChange} required />
        <h4><Trans>Welcome Note*</Trans></h4>
        <LazyEditor
          sections={["inlineStyleAndLink"]}
          initialValue={welcomeNote}
          onChange={onWelcomeNoteChange}
          name="welcomeNote"
          ref={editorRef}
        />
        <div className="color-field">
          <h4><Trans>Primary Color*</Trans></h4>
          <MaterialPicker name="color" onChange={handleChange} color={color} />
          <HuePicker onChange={handleChange} color={color} />
        </div>
      </div>
      <div className="col-md-6 Design-Preview">
        <a className="Design-Preview__Link" href="https://demo.culturehq.com" target="_blank" rel="noopener noreferrer">
          <Icon icon="page" />
          <Trans>View an example culture page here</Trans>
        </a>
        <div className="Preview">
          <img className="Preview-Browser" src={landingPreviewHeader} alt={t("Browser header")} />
          <div className="Preview-Header__Image">
            <img className="Preview-Header__Image--Header" src={selectedHeaderImage ? getSelectedHeaderImage() : landingPreviewHeaderBG} alt={t("Browser header")} />
            <div className="Preview-Header__Overlay">
              <h1 className="Preview-Header__Text" style={{ backgroundImage: `linear-gradient(120deg, ${color} 0%, ${color}38 100%)` }}>
                {headerText || t("Enter your header text here...")}
              </h1>
            </div>
          </div>
          <div className="Preview-Body">
            <div className="Preview-Browser__Welcome">
              <div className="Preview-Welcome" style={{ borderColor: `${color}` }}>
                <Icon className="Preview-Welcome__Icon" color={color} icon="quote" />
                <LazyEditorOutput className="Preview-Welcome__Note" output={welcomeNote || " "} showTags={false} />
              </div>
            </div>
            <img className="Preview-Browser__Body" src={landingPreviewBody} alt={t("Browser body")} />
            <img className="Preview-Logo" src={selectedLogo ? getSelectedLogo() : landingEmptyLogo} alt={t("Logo image")} />
          </div>
        </div>
      </div>
    </div>
  );
};

const EditLandingPage = ({ landingPage, onSubmitted, botToken }) => {
  const editorRef = useRef(null);
  const { profile } = useProfile();
  const labels = useFieldsLabels();

  const [isReady, setIsReady] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedStories, setSelectedStories] = useState([]);
  const [changed, setChanged] = useState(false);
  const [error, setError] = useState();
  const [valuesLocal, setValuesLocal] = useState({
    logoUrl: undefined,
    headerImageUrl: undefined,
    headerText: "",
    welcomeNote: "",
    primaryColor: "#79b17d"
  });
  const [isLive, setIsLive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [approvingContent, setApprovingContent] = useState(false);
  const [subdomain, setSubdomain] = useState();

  useEffect(
    () => {
      localStorage.setItem("departmentsLabel", JSON.stringify(labels.departments.plural.toLowerCase()));
      localStorage.setItem("companiesLabel", JSON.stringify(labels.companies.plural.toLowerCase()));
    }, [labels]
  );

  useEffect(
    () => {
      const orgName = getOrganizationSubdomain(profile.organization);
      setSubdomain(orgName);

      localStorage.setItem("orgName", JSON.stringify(orgName));
      localStorage.setItem("isEducational", JSON.stringify(profile.organization.isEducational));
    }, [profile.organization]
  );

  useEffect(
    () => {
      const setLocalStorage = values => {
        Object.keys(values).forEach(key => {
          localStorage.setItem(key, JSON.stringify(values[key]));
        });
        localStorage.setItem("isLogoBlob", JSON.parse(false));
        localStorage.setItem("isHeaderImageBlob", JSON.parse(false));
      };

      if (landingPage) {
        const data = valuesLocal;

        let ready = true;
        Object.keys(data).forEach(key => {
          if (!landingPage[key] && landingPage[key] !== false) {
            ready = false;
          }
        });
        setIsReady(ready);
        setValuesLocal(landingPage);
        setIsLive(landingPage.live);
        setLocalStorage({
          ...landingPage,
          stories: landingPage.stories.slice(0, 24),
          users: landingPage.users.slice(0, 24)
        });
      } else {
        setLocalStorage(valuesLocal);
        setEditMode(true);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [landingPage]
  );

  const onChange = useCallback((key, value) => {
    setChanged(true);
    const data = valuesLocal;
    data[key] = value;

    let ready = true;
    Object.keys(data).forEach(key => {
      if (!data[key]) {
        ready = false;
      }
    });
    setIsReady(ready);

    if (key === "logoUrl") {
      const reader = new FileReader();

      reader.onload = event => {
        localStorage.setItem("logoUrl", event.target.result);
      };

      reader.readAsDataURL(value);
      localStorage.setItem("isLogoBlob", JSON.parse(true));
    } else if (key === "headerImageUrl") {
      const reader = new FileReader();

      reader.onload = event => {
        localStorage.setItem("headerImageUrl", event.target.result);
      };

      reader.readAsDataURL(value);
      localStorage.setItem("isHeaderImageBlob", JSON.parse(true));
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
    setValuesLocal(data);
  }, [setChanged, valuesLocal, setValuesLocal]);

  const onSelectedUsers = selectedUsers => {
    localStorage.setItem("users", JSON.stringify(selectedUsers));
    setSelectedUsers(selectedUsers);
  };

  const onSelectedStories = selectedStories => {
    localStorage.setItem("stories", JSON.stringify(selectedStories));
    setSelectedStories(selectedStories);
  };

  const getResponse = useCallback(
    async (logo, headerImage, headerText, welcomeNote, primaryColor, live) => {
      if (landingPage) {
        const { landingPage: newLandingPage } = await client.updateLandingPage({
          landingPageId: landingPage.id,
          logoUrl: logo,
          headerImageUrl: headerImage,
          headerText,
          welcomeNote,
          primaryColor,
          live
        });
        setIsLive(newLandingPage.live);
        onSubmitted(newLandingPage);
      } else {
        const { landingPage: newLandingPage } = await client.createLandingPage({
          logoUrl: logo,
          headerImageUrl: headerImage,
          headerText,
          welcomeNote,
          primaryColor,
          live,
          users: selectedUsers,
          stories: selectedStories
        });
        setIsLive(newLandingPage.live);
        onSubmitted(newLandingPage);
      }
      setChanged(false);
      setLoading(false);
      setEditMode(false);
    },
    [landingPage, onSubmitted, selectedUsers, selectedStories]
  );

  const onAWSUploadedFile = useCallback(
    async (logo, headerImage, headerText, welcomeNote, primaryColor, live) => {
      await getResponse(logo, headerImage, headerText, welcomeNote, primaryColor, live);
    },
    [getResponse]
  );

  const waitForS3 = useCallback(
    async (ms, logo, headerImage, headerText, welcomeNote, primaryColor, live) => (
      new Promise(_ => {
        // This timeout is to allow AWS to upload the file, in this point,
        // AWs has responded with a 200 status, but we need to take care of
        // S3 Consistency Model
        setTimeout(
          onAWSUploadedFile, ms, logo, headerImage, headerText, welcomeNote, primaryColor, live
        );
      })
    ),
    [onAWSUploadedFile]
  );

  const handleSubmit = useCallback(
    async ({ logo, headerImage, headerText }) => {
      const welcomeNote = await editorRef.current.serialize();
      if (getText(welcomeNote).length > 0) {
        setError();
        if (!approvingContent) {
          setLoading(true);
          const headerImageUrl = headerImage
            ? await signUpload(headerImage)
            : landingPage.headerImageUrl;
          const logoUrl = logo
            ? await signUpload(logo)
            : landingPage.logoUrl;
          await waitForS3(
            2000,
            logoUrl,
            headerImageUrl,
            headerText,
            welcomeNote,
            valuesLocal.primaryColor,
            isLive
          );
        }
      } else {
        setError(t("Th welcome note is a required field"));
      }
    },
    [approvingContent, waitForS3, valuesLocal.primaryColor, landingPage, isLive]
  );

  const handleIsLive = async isLive => {
    setLoading(true);
    const welcomeNote = editorRef.current
      ? await editorRef.current.serialize() : valuesLocal.welcomeNote;
    const headerImageUrl = valuesLocal.headerImageUrl && typeof valuesLocal.headerImageUrl !== "string"
      ? await signUpload(valuesLocal.headerImageUrl)
      : landingPage.headerImageUrl;
    const logoUrl = valuesLocal.logoUrl && typeof valuesLocal.logoUrl !== "string"
      ? await signUpload(valuesLocal.logoUrl)
      : landingPage.logoUrl;
    await waitForS3(
      2000,
      logoUrl,
      headerImageUrl,
      valuesLocal.headerText,
      welcomeNote,
      valuesLocal.primaryColor,
      isLive
    );
  };

  const tags = getTags(
    landingPage?.values,
    landingPage?.interests,
    landingPage?.skills,
    landingPage?.classYears,
    landingPage?.clubs
  );

  if (loading) return <Loader loading />;

  return (
    <>
      <Prompt
        when={changed}
        message="You have unsaved changes, are you sure you want to leave?"
      />
      <div>
        <Form onSubmit={handleSubmit} initialValues={landingPage}>
          <div className="LandingPage--Edit">
            <div>
              <div className="LandingPage--Title__Container">
                <h1 className="LandingPage--Title"><Trans>Primary Culture Page</Trans></h1>
                {isLive
                  ? <span className="LandingPage--Tag Live"><Trans>LIVE</Trans></span>
                  : <span className="LandingPage--Tag"><Trans>PAUSED</Trans></span>}
              </div>
              {isLive
                ? <a className="LandingPage--Link__Filled" href={`https://${subdomain}.culturehq.com`} target="_blank" rel="noopener noreferrer">{`https://${subdomain}.culturehq.com`} <Icon icon="android-open" /></a>
                : <a className="LandingPage--Link__Empty" style={{ pointerEvents: "none" }} href={`https://${subdomain}.culturehq.com`} target="_blank" rel="noopener noreferrer">{`https://${subdomain}.culturehq.com`}</a>}
            </div>
            <div className="LandingPage--Edit_Buttons">
              {isLive && !editMode && (
                <EmbedStories
                  botToken={botToken}
                  orgName={subdomain}
                  selectedStories={landingPage.stories}
                />
              )}
              {isLive && !editMode && (
                <CopyLink link={`https://${profile.organization.name.replace(/\s+/g, "-").toLowerCase()}.culturehq.com`} shareButton />
              )}
              {editMode && (
                <Link
                  className="chq-btn Gray-Button"
                  to="/admin/landing-page/preview"
                  target="_blank"
                >
                  <Icon icon="preview-filled" />
                  <Trans>Preview Page</Trans>
                </Link>
              )}
              {isLive && (
                <Button
                  className="Gray-Button"
                  icon="pause"
                  onClick={() => handleIsLive(false)}
                >
                  <Trans>Pause</Trans>
                </Button>
              )}
              {editMode ? (
                <SubmitButton className={isLive ? "" : "Gray-Button"} icon="checkmark">
                  {submitting => (submitting ? t("Saving...") : t("Save and Close"))}
                </SubmitButton>
              ) : (
                <Button
                  className={isLive ? "" : "Gray-Button"}
                  icon="edit"
                  onClick={() => setEditMode(true)}
                >
                  <Trans>Edit Page</Trans>
                </Button>
              )}
              {(!landingPage || !isLive) && (
                <>
                  {isReady
                    ? (
                      <ChqModalConfirm
                        onAccept={() => handleIsLive(true)}
                        trigger={onTrigger => (
                          <Button icon="play-filled" fillParent onClick={onTrigger}>
                            <Trans>Set Live</Trans>
                          </Button>
                        )}
                      >
                        <Trans>Are you ready to set your page live with these changes?</Trans>
                      </ChqModalConfirm>
                    ) : (
                      <Tooltip bottom tip={t("Finish creating your page before setting it live")}>
                        <Button icon="play-filled" fillParent disabled>
                          <Trans>Set Live</Trans>
                        </Button>
                      </Tooltip>
                    )}
                </>
              )}
            </div>
          </div>
          {editMode ? (
            <>
              <Accordian
                icon="art-palette"
                title={t("Design Page")}
                subtitle={t("Add your logo, header image and text, and primary color.")}
                className="base-menu"
              >
                <DesingSection
                  editorRef={editorRef}
                  error={error}
                  onChange={onChange}
                  landingPage={landingPage}
                />
              </Accordian>
              <Accordian
                icon="file-filled"
                title={t("Select Content")}
                subtitle={t("Choose specific profiles and stories to showcase on your external page.")}
                className="base-menu"
              >
                <ApproveSection
                  landingPage={landingPage}
                  onSelectedStories={onSelectedStories}
                  onSelectedUsers={onSelectedUsers}
                  setApprovingContent={setApprovingContent}
                  onLandingPageUpdated={onSubmitted}
                />
              </Accordian>
            </>
          ) : (
            <>
              {landingPage && (
                <div className="LandingPage--Preview Design-Preview">
                  <span className="LandingPage--Preview__Title">
                    <h1><Trans>Latest Page</Trans></h1>
                    {" "}
                    -
                    {" "}
                    <h4><Trans>last saved</Trans>{" "}<Time time={landingPage.updatedAt} format="%m/%d/%Y %l:%M %p" keepYear /></h4>
                  </span>
                  <div className="Preview">
                    <div className="Preview-Header__Image" style={{ backgroundImage: `url(${landingPage.headerImageUrl}` }}>
                      <div className="Preview-Header__Overlay">
                        <h1 className="Preview-Header__Text" style={{ backgroundImage: `linear-gradient(120deg, ${landingPage.primaryColor} 0%, ${landingPage.primaryColor}38 100%)` }}>
                          {landingPage.headerText}
                        </h1>
                      </div>
                    </div>
                    <div className="Preview-Body">
                      <div className="col-xs-3 Preview-LeftColumn">
                        <img className="Preview-Logo" src={landingPage.logoUrl} alt={t("Logo image")} />
                        <div className="Preview-Tags">
                          <Tags
                            entityFormat
                            tags={tags}
                            title={t("TAGS")}
                            icon="heart-ol"
                            color={landingPage.primaryColor}
                          />
                          {landingPage.organization.isEducational ? (
                            <>
                              <Tags
                                type="programs"
                                tags={landingPage.programs}
                                title={t("PROGRAMS")}
                                icon="ios-star-outline"
                                color={landingPage.primaryColor}
                              />
                              <Tags
                                type="degrees"
                                tags={landingPage.degrees}
                                title={t("DEGREES")}
                                icon="mortarboard"
                                color={landingPage.primaryColor}
                              />
                            </>
                          ) : (
                            <>
                              <Tags
                                type="departments"
                                tags={landingPage.departments}
                                title={labels.departments.plural.toUpperCase()}
                                icon="ios-people-outline"
                                color={landingPage.primaryColor}
                              />
                              {canViewCompaniesField(profile.organization) && (
                                <Tags
                                  type="companies"
                                  tags={landingPage.companies}
                                  title={t("COMPANIES")}
                                  icon="building"
                                  color={landingPage.primaryColor}
                                />
                              )}
                              <Tags
                                type="locations"
                                tags={landingPage.locations}
                                title={t("LOCATIONS")}
                                icon="loc-ol"
                                color={landingPage.primaryColor}
                              />
                            </>
                          )}
                        </div>
                      </div>
                      <div className="col-xs-9">
                        <div className="Preview-Browser__Welcome">
                          <div className="Preview-Welcome" style={{ borderColor: `${landingPage.primaryColor}` }}>
                            <Icon className="Preview-Welcome__Icon" color={landingPage.primaryColor} icon="quote" />
                            <LazyEditorOutput className="Preview-Welcome__Note" output={landingPage.welcomeNote || " "} showTags={false} />
                          </div>
                        </div>
                        <div className="Preview-Browser__People">
                          <h2><Trans>Meet our people</Trans></h2>
                          <UsersSlider users={landingPage.users} />
                        </div>
                      </div>
                    </div>
                    <div className="Preview__Overlay" />
                  </div>
                  <div>
                    <h2><Trans>Profile Spotlights</Trans></h2>
                    <DisplayUsers selectedUsers={landingPage.users} />
                    <h2><Trans>Story Spotlights</Trans></h2>
                    <DisplayStories selectedStories={landingPage.stories} />
                  </div>
                </div>
              )}
            </>
          )}
        </Form>
      </div>
    </>
  );
};
const EmployerBranding = () => {
  const [createLandingPage, setCreateLandingPage] = useState(false);
  const [landingPage, setLandingPage] = useState(undefined);

  const onCreateLandingPage = () => {
    setCreateLandingPage(true);
  };

  const { fetching, json: { landingPages = [], botToken } } = useFetch(
    () => client.listLandingPages(),
    []
  );

  const handleUpdatedLandingPage = updated => {
    setLandingPage(updated);
  };
  if (fetching) {
    return <Loader loading />;
  }

  return (
    <div className="LandingPage">
      {landingPages.length === 0 && !createLandingPage
        ? <EmptyState onCreateLandingPage={onCreateLandingPage} />
        : (
          <EditLandingPage
            landingPage={landingPage || landingPages[0]}
            botToken={botToken}
            onSubmitted={handleUpdatedLandingPage}
          />
        )}
    </div>
  );
};

export default EmployerBranding;
